import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, LogOut, ExternalLink, RotateCcw, Check, KeyRound } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useContent } from "../../context/ContentContext";
import FieldEditor from "../../components/admin/FieldEditor";

const SECTION_LABELS = {
  brand: "Brand", header: "Header", hero: "Hero", stats: "Stats", trustBadges: "Trust Badges",
  methodComparison: "Method Comparison", greenCertified: "Green-Certified", editorsChoice: "Editor's Choice",
  howItWorks: "How It Works", providerDirectory: "Provider Directory", cities: "Cities Section",
  categories: "Categories Section", ctaBand: "CTA Band", faqHome: "FAQ Heading", footer: "Footer",
  compare: "Compare Page", providersPage: "Providers Page", locations: "Locations Page",
  resources: "Resources Page", about: "About Page", faqs: "FAQ Items",
};

export default function AdminDashboard() {
  const { isAuthed, username, logout, authAxios } = useAdminAuth();
  const { c, refresh } = useContent();
  const navigate = useNavigate();

  const [draft, setDraft] = useState(null);
  const [active, setActive] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    if (!isAuthed) navigate("/admin");
  }, [isAuthed, navigate]);

  useEffect(() => {
    authAxios.get("/content").then((res) => setDraft(res.data)).catch(() => setDraft(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(() => (draft ? Object.keys(draft) : []), [draft]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await authAxios.put("/content", { content: draft });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset all content to the original defaults? This cannot be undone.")) return;
    const res = await authAxios.post("/content/reset");
    setDraft(res.data);
    await refresh();
  };

  if (!draft) {
    return <div className="flex min-h-screen items-center justify-center text-slate-500">Loading content...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--navy)] text-white font-extrabold">O</div>
          <div>
            <div className="font-display text-[15px] font-extrabold text-[var(--ink)]">Content Manager</div>
            <div className="text-[11px] text-slate-400">Signed in as {username}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPwOpen(true)} className="hidden items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-semibold text-[var(--ink)] hover:bg-slate-50 sm:flex"><KeyRound size={15} /> Password</button>
          <a href="/" target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-semibold text-[var(--ink)] hover:bg-slate-50 sm:flex"><ExternalLink size={15} /> View site</a>
          <button onClick={resetDefaults} className="hidden items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-semibold text-slate-500 hover:bg-slate-50 md:flex"><RotateCcw size={15} /> Reset</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[var(--brand-blue)] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[var(--brand-blue-hover)] disabled:opacity-60">
            {saved ? <><Check size={15} /> Saved</> : <><Save size={15} /> {saving ? "Saving..." : "Save changes"}</>}
          </button>
          <button onClick={() => { logout(); navigate("/admin"); }} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100" title="Logout"><LogOut size={16} /></button>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1200px] gap-6 px-4 py-6 lg:px-8">
        <aside className="hidden w-56 shrink-0 lg:block">
          <nav className="sticky top-[76px] space-y-1">
            {sections.map((s) => (
              <button key={s} onClick={() => setActive(s)} className={`block w-full rounded-lg px-3 py-2 text-left text-[13.5px] font-medium transition-colors ${active === s ? "bg-[var(--navy)] text-white" : "text-slate-600 hover:bg-white"}`}>
                {SECTION_LABELS[s] || s}
              </button>
            ))}
          </nav>
        </aside>

        <main className="flex-1">
          <div className="mb-4 lg:hidden">
            <select value={active} onChange={(e) => setActive(e.target.value)} className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-[14px] font-medium">
              {sections.map((s) => <option key={s} value={s}>{SECTION_LABELS[s] || s}</option>)}
            </select>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
            <h2 className="font-display text-[20px] font-extrabold text-[var(--ink)]">{SECTION_LABELS[active] || active}</h2>
            <p className="mb-6 mt-1 text-[13px] text-slate-400">Edit the text and images for this section, then click Save changes.</p>
            <FieldEditor fieldKey={active} value={draft[active]} onChange={(v) => setDraft({ ...draft, [active]: v })} />
          </div>
        </main>
      </div>

      {pwOpen && <ChangePassword onClose={() => setPwOpen(false)} authAxios={authAxios} />}
    </div>
  );
}

function ChangePassword({ onClose, authAxios }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async () => {
    setMsg("");
    try {
      await authAxios.post("/auth/change-password", { current_password: cur, new_password: next });
      setMsg("Password updated successfully.");
      setCur(""); setNext("");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to update password");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5" onClick={onClose}>
      <div className="w-full max-w-[380px] rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-[18px] font-bold text-[var(--ink)]">Change password</h3>
        {msg && <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-600">{msg}</div>}
        <label className="mt-4 block text-[13px] font-semibold">Current password</label>
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3 text-[14px] outline-none focus:border-[var(--brand-blue)]" />
        <label className="mt-3 block text-[13px] font-semibold">New password</label>
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3 text-[14px] outline-none focus:border-[var(--brand-blue)]" />
        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-[14px] font-semibold text-[var(--ink)] hover:bg-slate-50">Cancel</button>
          <button onClick={submit} className="flex-1 rounded-lg bg-[var(--brand-blue)] px-4 py-2.5 text-[14px] font-semibold text-white hover:bg-[var(--brand-blue-hover)]">Update</button>
        </div>
      </div>
    </div>
  );
}
